import React, { FC, useEffect, useId, useState } from "react";
import { FormSelectInput } from "stentor-models";

import { Styleable } from "../../types";

import { FormFieldProps } from "../FormDisplay";
import { CheckBoxIcon, CheckBoxOutlineIcon, RadioCheckedIcon, RadioUncheckedIcon } from "../icons";

import styles from "./FormFieldSelect.module.scss";

export interface FormSelectProps extends FormFieldProps {
    readonly field: Styleable<FormSelectInput>;
}

export const FormFieldSelect: FC<FormSelectProps> = (props) => {
    const { field, onFieldChange, errorMessage } = props;

    const [selectValue, setSelectValue] = useState<string[]>([]); // array for checkboxes
    const [radioValue, setRadioValue] = useState<string>(""); // single value for radio

    const onCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, checked } = e.target;
        setSelectValue((prevOptions) => {
            if (checked) {
                return [...prevOptions, name];
            } else {
                return prevOptions.filter((option) => option !== name);
            }
        });
    };

    const onRadioChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setRadioValue(e.target.value);
    };

    const id = `xapp-select-${useId()}`;
    const titleId = `${id}-title`;
    const helperId = `${id}-helper-text`;
    const invalid = !!errorMessage;

    useEffect(() => {
        if (field.radio) {
            onFieldChange(field.name, radioValue ? [radioValue] : []);
        } else {
            onFieldChange(field.name, selectValue);
        }
    }, [selectValue, radioValue, field.name, field.radio, onFieldChange]);

    return (
        <div style={{ ...field.style }}>
            <div
                className={`${styles.xapp_select_title_container}${!field.title ? "__empty" : ""}`}>
                <p id={titleId}>{field.title}</p>
            </div>

            {/* Native radios sharing one name get arrow-key movement and single selection from
                the browser; the group names them by the step's question. */}
            <div
                className={styles.xapp_select_container}
                role={field.radio ? "radiogroup" : "group"}
                // A radio cannot carry aria-invalid; its group can. Checkboxes carry their own.
                aria-invalid={(field.radio && invalid) || undefined}
                aria-labelledby={field.title ? titleId : undefined}
                aria-describedby={invalid ? helperId : undefined}>
                {field.items.map((item) => {
                    const checked = field.radio ? radioValue === item.id : selectValue.includes(item.id);
                    const Icon = field.radio
                        ? checked
                            ? RadioCheckedIcon
                            : RadioUncheckedIcon
                        : checked
                        ? CheckBoxIcon
                        : CheckBoxOutlineIcon;

                    return (
                        <label key={item.id} className={styles.xapp_select_option}>
                            <span
                                className={`${styles.xapp_checkbox_select} ${
                                    checked ? styles.xapp_checkbox_select_checked : ""
                                }`}>
                                {field.radio ? (
                                    <input
                                        type="radio"
                                        className={styles.xapp_select_input}
                                        name={field.name}
                                        value={item.id}
                                        checked={checked}
                                        onChange={onRadioChange}
                                    />
                                ) : (
                                    <input
                                        type="checkbox"
                                        className={styles.xapp_select_input}
                                        name={item.id}
                                        checked={checked}
                                        onChange={onCheckboxChange}
                                        aria-invalid={invalid || undefined}
                                    />
                                )}
                                <Icon className={styles.xapp_select_icon} />
                            </span>
                            <span className={styles.xapp_select_label}>{item.label}</span>
                        </label>
                    );
                })}
            </div>

            {errorMessage && (
                <p id={helperId} className={styles.xapp_select_helper}>
                    {errorMessage}
                </p>
            )}
        </div>
    );
};

export default FormFieldSelect;
